<template>
  <section class="flex h-full min-h-[420px] flex-col rounded-[20px] border border-white/90 bg-white/80 shadow-[0_16px_48px_rgba(30,64,115,0.055)] backdrop-blur-xl">
    <div class="border-b border-zinc-200 p-4">
      <div class="flex items-center justify-between gap-3">
        <div>
          <h3 class="text-base font-semibold text-zinc-900">
            Member Activity
          </h3>
          <p class="mt-0.5 text-xs text-zinc-500">
            Live membership events
          </p>
        </div>
        <div class="inline-flex rounded-lg border border-zinc-200 bg-zinc-50 p-1 text-xs font-semibold text-zinc-600">
          <button
            type="button"
            class="rounded-md px-2.5 py-1.5 transition"
            :class="rangeKey === '1h' ? 'bg-[#2D8CFF] text-white shadow-sm' : 'hover:bg-white'"
            @click="setRange('1h')"
          >
            1h
          </button>
          <button
            type="button"
            class="rounded-md px-2.5 py-1.5 transition"
            :class="rangeKey === '24h' ? 'bg-[#2D8CFF] text-white shadow-sm' : 'hover:bg-white'"
            @click="setRange('24h')"
          >
            24h
          </button>
        </div>
      </div>
    </div>

    <div
      ref="scrollEl"
      class="app-scroll min-h-0 flex-1 overflow-y-auto p-4"
      @scroll="onScroll"
    >
      <div
        v-if="isLoading && !activities.length"
        class="space-y-4"
      >
        <div
          v-for="index in 6"
          :key="`activity-skeleton-${index}`"
          class="flex animate-pulse gap-3"
        >
          <div class="h-10 w-10 rounded-full bg-zinc-200" />
          <div class="flex-1 space-y-2">
            <div class="h-3 w-1/2 rounded bg-zinc-200" />
            <div class="h-3 w-4/5 rounded bg-zinc-200" />
            <div class="h-3 w-1/4 rounded bg-zinc-200" />
          </div>
        </div>
      </div>
      <div
        v-else-if="errorMessage && !activities.length"
        class="rounded-lg bg-red-50 px-3 py-4 text-sm text-red-600"
      >
        {{ errorMessage }}
      </div>
      <div
        v-else-if="!activities.length"
        class="rounded-lg bg-zinc-50 px-3 py-6 text-center text-sm text-zinc-500"
      >
        No member activity in this range.
      </div>
      <div
        v-else
        class="space-y-4"
      >
        <article
          v-for="activity in activities"
          :key="activity.id || activity.activityId || `${activity.memberId}-${activity.occurredAt}-${activity.eventType}`"
          class="flex gap-3"
        >
          <img
            :src="activityAvatarSrc(activity)"
            :alt="`${activity.memberName || activity.memberEmail || 'Member'} avatar`"
            class="h-10 w-10 shrink-0 rounded-full bg-zinc-100 object-cover ring-1 ring-inset ring-zinc-200/70"
            @error="useDefaultActivityAvatar"
          >
          <div class="min-w-0 flex-1">
            <div class="flex items-start justify-between gap-3">
              <h4 class="truncate text-sm font-semibold text-zinc-900">
                {{ activity.memberName || activity.memberEmail || 'Member' }}
              </h4>
              <span class="shrink-0 text-xs text-zinc-400">{{ timeAgo(activity.occurredAt || activity.createdAt) }}</span>
            </div>
            <p class="mt-0.5 text-sm text-zinc-600">
              {{ activity.description || prettifyAction(activity.action || activity.eventType) }}
            </p>
            <span
              class="mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold"
              :class="featureClass(activity.featureType || activity.eventType)"
            >
              {{ featureLabel(activity.featureType || activity.eventType) }}
            </span>
          </div>
        </article>
      </div>

      <button
        v-if="canLoadMore"
        type="button"
        class="mt-4 w-full rounded-lg bg-zinc-100 px-3 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-200"
        :disabled="isLoadingMore"
        @click="loadMore"
      >
        {{ isLoadingMore ? 'Loading...' : 'Load more' }}
      </button>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { fetchCreatorActivities } from '@/services/creatorEngagementService';
import { useWebsocketStore } from '@/stores/websocketStore';
import defaultMemberAvatar from '@/assets/media/images/default-avatar.png';

const websocketStore = useWebsocketStore();
const rangeKey = ref('1h');
const activities = ref([]);
const total = ref(0);
const offset = ref(0);
const pageSize = 30;
const isLoading = ref(false);
const isLoadingMore = ref(false);
const errorMessage = ref('');
const scrollEl = ref(null);
let unsubscribeActivity = null;
let stopConnectionWatch = null;
let initialLoadComplete = false;
let loadVersion = 0;

const canLoadMore = computed(() => activities.value.length < total.value);

const normalizeActivity = (activity = {}) => ({
  id: Number(activity.id || activity.activityId || 0),
  activityId: Number(activity.activityId || activity.id || 0),
  memberId: Number(activity.memberId || 0) || null,
  memberName: String(activity.memberName || '').trim(),
  memberEmail: String(activity.memberEmail || '').trim(),
  memberAvatarUrl: String(
    activity.memberAvatarUrl
      || activity.member_avatar_url
      || activity.avatarUrl
      || activity.profilePic
      || activity.profile_pic
      || ''
  ).trim(),
  featureType: String(activity.featureType || '').trim(),
  eventType: String(activity.eventType || activity.type || '').trim(),
  action: String(activity.action || '').trim(),
  description: String(activity.description || '').trim(),
  occurredAt: activity.occurredAt || activity.createdAt || null,
  createdAt: activity.createdAt || null,
});

const activityKey = (activity = {}) => (
  activity.id
  || activity.activityId
  || `${activity.memberId}-${activity.occurredAt}-${activity.eventType}`
);

const mergeActivities = (incoming = [], { prepend = false } = {}) => {
  const existingKeys = new Set(activities.value.map(activityKey).filter(Boolean));
  const map = new Map();
  let addedCount = 0;
  const ordered = prepend
    ? [...incoming.map(normalizeActivity), ...activities.value]
    : [...activities.value, ...incoming.map(normalizeActivity)];

  ordered.forEach((activity) => {
    const key = activityKey(activity);
    if (!key || map.has(key)) return;
    if (!existingKeys.has(key)) addedCount += 1;
    map.set(key, activity);
  });

  activities.value = Array.from(map.values()).sort((a, b) => {
    const aTime = new Date(a.occurredAt || a.createdAt || 0).getTime();
    const bTime = new Date(b.occurredAt || b.createdAt || 0).getTime();
    return bTime - aTime;
  });

  return addedCount;
};

async function loadInitial() {
  const requestVersion = ++loadVersion;
  const requestRange = rangeKey.value;
  isLoading.value = true;
  errorMessage.value = '';
  offset.value = 0;
  total.value = 0;
  activities.value = [];
  try {
    const data = await fetchCreatorActivities({
      rangeKey: requestRange,
      limit: pageSize,
      offset: 0,
    });
    if (requestVersion !== loadVersion || requestRange !== rangeKey.value) return;
    mergeActivities(Array.isArray(data?.activities) ? data.activities : []);
    total.value = Math.max(Number(data?.total || 0), activities.value.length);
    offset.value = activities.value.length;
  } catch (error) {
    if (requestVersion !== loadVersion) return;
    errorMessage.value = error?.message || 'Failed to load activity';
  } finally {
    if (requestVersion === loadVersion) {
      isLoading.value = false;
      initialLoadComplete = true;
    }
  }
}

async function refreshLatest() {
  if (!initialLoadComplete || isLoading.value) return;
  try {
    const data = await fetchCreatorActivities({
      rangeKey: rangeKey.value,
      limit: pageSize,
      offset: 0,
    });
    const addedCount = mergeActivities(
      Array.isArray(data?.activities) ? data.activities : [],
      { prepend: true }
    );
    offset.value += addedCount;
    total.value = Math.max(Number(data?.total || 0), activities.value.length);
    errorMessage.value = '';
  } catch (error) {
    console.warn('[member-activity] Failed to reconcile recent activities:', error);
  }
}

async function loadMore() {
  if (isLoadingMore.value || !canLoadMore.value) return;
  isLoadingMore.value = true;
  try {
    const data = await fetchCreatorActivities({
      rangeKey: rangeKey.value,
      limit: pageSize,
      offset: offset.value,
    });
    total.value = Number(data?.total || total.value || 0);
    const nextRows = Array.isArray(data?.activities) ? data.activities : [];
    mergeActivities(nextRows);
    offset.value += nextRows.length;
  } catch (error) {
    errorMessage.value = error?.message || 'Failed to load more activity';
  } finally {
    isLoadingMore.value = false;
  }
}

function setRange(nextRange) {
  if (rangeKey.value === nextRange) return;
  rangeKey.value = nextRange;
  loadInitial();
}

function onScroll() {
  const el = scrollEl.value;
  if (!el || !canLoadMore.value) return;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
    loadMore();
  }
}

function onLiveActivity(payload) {
  const next = normalizeActivity(payload || {});
  if (!next.eventType && !next.action && !next.description) return;
  const addedCount = mergeActivities([next], { prepend: true });
  if (addedCount > 0) {
    total.value += addedCount;
    offset.value += addedCount;
  }
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') void refreshLatest();
}

function activityAvatarSrc(activity = {}) {
  const candidate = String(activity.memberAvatarUrl || '').trim();
  if (!candidate || candidate === 'null' || candidate === 'undefined') return defaultMemberAvatar;
  return candidate;
}

function useDefaultActivityAvatar(event) {
  const image = event?.currentTarget;
  if (!image || image.src === defaultMemberAvatar) return;
  image.src = defaultMemberAvatar;
}

function prettifyAction(value = '') {
  return String(value || 'member_activity')
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function featureLabel(value = '') {
  const token = String(value || '').toLowerCase();
  if (token.includes('course')) return 'Course';
  if (token.includes('quiz')) return 'Quiz';
  if (token.includes('newsfeed')) return 'Newsfeed';
  if (token.includes('video')) return 'Video';
  if (token.includes('flix')) return 'Flix';
  if (token.includes('webinar')) return 'Webinar';
  if (token.includes('meet')) return 'Meet';
  if (token.includes('store')) return 'Store';
  if (token.includes('subscription') || token.includes('payment')) return 'Membership';
  if (token.includes('crypto')) return 'Crypto';
  return 'Activity';
}

function featureClass(value = '') {
  const token = String(value || '').toLowerCase();
  if (token.includes('payment') || token.includes('subscription')) return 'bg-emerald-50 text-emerald-700';
  if (token.includes('course')) return 'bg-indigo-50 text-indigo-700';
  if (token.includes('quiz')) return 'bg-fuchsia-50 text-fuchsia-700';
  if (token.includes('newsfeed')) return 'bg-sky-50 text-sky-700';
  if (token.includes('video') || token.includes('flix')) return 'bg-violet-50 text-violet-700';
  if (token.includes('webinar')) return 'bg-rose-50 text-rose-700';
  if (token.includes('meet')) return 'bg-blue-50 text-blue-700';
  if (token.includes('store')) return 'bg-amber-50 text-amber-700';
  return 'bg-zinc-100 text-zinc-700';
}

function timeAgo(value) {
  const date = new Date(value || 0);
  const ms = Date.now() - date.getTime();
  if (!Number.isFinite(ms)) return '';
  const minutes = Math.max(0, Math.floor(ms / 60000));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

onMounted(() => {
  unsubscribeActivity = websocketStore.subscribe('creator:memberActivity', onLiveActivity);
  stopConnectionWatch = watch(
    () => websocketStore.isConnected,
    (isConnected, wasConnected) => {
      if (isConnected && wasConnected === false) void refreshLatest();
    }
  );
  document.addEventListener('visibilitychange', onVisibilityChange);
  void websocketStore.connect();
  void loadInitial();
});

onBeforeUnmount(() => {
  if (typeof unsubscribeActivity === 'function') unsubscribeActivity();
  if (typeof stopConnectionWatch === 'function') stopConnectionWatch();
  document.removeEventListener('visibilitychange', onVisibilityChange);
});
</script>

<style scoped>
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  scrollbar-gutter: stable;
}

.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.app-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.541) transparent;
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.479);
}

.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>
